import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDoctorPatientClinicalChart } from '../api/doctorPatientsApi';
import { formatDate, formatDateTime, formatPatientName, normalizeUiLocale } from '../utils/locale';

const PROFILE_CATEGORIES = [
  'allergy',
  'medication',
  'condition',
  'surgery',
  'family_history',
  'obstetric_history',
  'social_history',
  'other_history'
];

const PatientDetail = () => {
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);

  const fetchChart = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getDoctorPatientClinicalChart(id, {
        visitLimit: 30,
        historyLimit: 10
      });
      setChart(data);
    } catch (requestError) {
      console.error('获取患者临床档案失败:', requestError);
      setError(`${t('patients.fetchChartError')}: ${requestError.response?.data?.error || t('common.unknownError')}`);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  const patient = chart?.patient;
  const visits = chart?.visits || [];
  const clinicalProfile = chart?.clinical_profile;
  const historyContext = chart?.clinical_history_context || {};

  const groupedFacts = useMemo(() => {
    const categories = clinicalProfile?.categories || {};
    const fallbackFacts = Array.isArray(clinicalProfile?.facts) ? clinicalProfile.facts : [];
    return PROFILE_CATEGORIES.map((category) => ({
      category,
      facts: categories[category] || fallbackFacts.filter((fact) => fact?.category === category)
    })).filter((group) => group.facts.length > 0);
  }, [clinicalProfile]);

  const getPatientName = () => {
    if (!patient) {
      return t('common.unknown');
    }
    return formatPatientName(patient, locale, patient.patient_id || `#${patient.id}`);
  };

  const formatPatientDateTime = (value) => {
    if (!value) {
      return '-';
    }
    return formatDateTime(value, locale) || '-';
  };

  const formatDateOnly = (value) => {
    if (!value) {
      return '-';
    }
    return formatDate(value, locale) || value;
  };

  const formatVisitType = (visitType) => {
    if (visitType === 'follow_up') return t('patients.visitTypeFollowUp');
    if (visitType === 'checkup') return t('patients.visitTypeCheckup');
    if (visitType === 'initial') return t('patients.visitTypeInitial');
    return t('patients.visitTypeUnknown');
  };

  const formatVisitStatus = (status) => {
    if (status === 'complete') return t('patients.visitStatusComplete');
    if (status === 'ongoing') return t('patients.visitStatusOngoing');
    if (status === 'started') return t('patients.visitStatusStarted');
    if (status === 'refreshed') return t('patients.visitStatusRefreshed');
    if (status === 'canceled') return t('patients.visitStatusCanceled');
    return status || t('common.unknown');
  };

  const getVisitStatusClass = (status) => {
    if (status === 'complete') return 'status-pill status-pill--success';
    if (status === 'started' || status === 'ongoing') return 'status-pill status-pill--warning';
    if (status === 'canceled') return 'status-pill status-pill--danger';
    return 'status-pill status-pill--neutral';
  };

  const getProfileCategoryLabel = (category) => t(`patients.profileCategories.${category}`, category);

  const getTrendClass = (trend) => {
    if (trend === 'improved' || trend === 'slightly_improved') return 'status-pill status-pill--success';
    if (trend === 'worse') return 'status-pill status-pill--danger';
    if (trend === 'stable') return 'status-pill status-pill--cool';
    return 'status-pill status-pill--neutral';
  };

  const renderSnapshotList = (title, values = []) => {
    const safeValues = values.filter(Boolean);
    return (
      <div className="snapshot-list">
        <span className="snapshot-list__title">{title}</span>
        {safeValues.length === 0 ? (
          <span className="table-meta">{t('patients.noSnapshotItems')}</span>
        ) : (
          safeValues.map((value, index) => (
            <span key={`${title}-${index}`} className="snapshot-chip">
              {value}
            </span>
          ))
        )}
      </div>
    );
  };

  const renderSnapshot = (snapshot) => {
    if (!snapshot) {
      return (
        <div className="patient-timeline-empty">
          <span>{t('patients.noSnapshot')}</span>
        </div>
      );
    }

    return (
      <div className="snapshot-grid">
        <div className="snapshot-section">
          <h4>{t('patients.subjective')}</h4>
          {(snapshot.subjective_items || []).length === 0 ? (
            <p className="table-meta">{t('patients.noSnapshotItems')}</p>
          ) : (
            snapshot.subjective_items.map((item, index) => (
              <p key={`${item.title}-${index}`}>
                <strong>{item.title || t('patients.subjective')}:</strong> {item.detail || '-'}
              </p>
            ))
          )}
        </div>
        <div className="snapshot-section">
          <h4>{t('patients.objective')}</h4>
          {(snapshot.objective_entries || []).length === 0 ? (
            <p className="table-meta">{t('patients.noSnapshotItems')}</p>
          ) : (
            snapshot.objective_entries.map((entry, index) => (
              <p key={`${entry.key}-${index}`}>
                <strong>{entry.label || entry.key}:</strong> {entry.value}{entry.unit ? ` ${entry.unit}` : ''}
              </p>
            ))
          )}
        </div>
        <div className="snapshot-section snapshot-section--wide">
          {renderSnapshotList(t('reports.diagnosis'), snapshot.diagnosis || [])}
          {renderSnapshotList(t('reports.treatment'), snapshot.treatment || [])}
          {renderSnapshotList(t('reports.plan'), snapshot.plan || [])}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-shell patient-page">
        <section className="surface-panel">
          <div className="surface-panel__body loading-state">{t('common.loading')}</div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-shell patient-page">
        <div className="page-header">
          <button type="button" className="theme-button theme-button--secondary" onClick={() => navigate('/patients')}>
            {t('patients.backToPatients')}
          </button>
        </div>
        <div className="alert alert--danger">{error}</div>
      </div>
    );
  }

  return (
    <div className="page-shell patient-page patient-detail-page">
      <div className="page-header">
        <div className="page-title-block">
          <button type="button" className="table-link-button patient-back-button" onClick={() => navigate('/patients')}>
            ← {t('patients.backToPatients')}
          </button>
          <h1 className="page-title"><bdi dir="auto">{getPatientName()}</bdi></h1>
          <p className="page-subtitle">
            {t('patients.patientId')}: {patient?.patient_id || `#${patient?.id}`} · {t('patients.mrn')}: {patient?.mrn || '-'} · {patient?.gender || '-'} · {patient?.age || '-'} {t('patients.yearsOld')} · {t('patients.phone')}: {patient?.phone_e164 || patient?.phone || '-'}
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="theme-button theme-button--secondary" onClick={fetchChart}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      <div className="patient-chart-layout">
        <aside className="surface-panel patient-profile-rail">
          <div className="surface-panel__body stack">
            <div className="section-heading">
              <h2>{t('patients.profileFacts')}</h2>
              <span className="table-meta">{t('patients.readOnly')}</span>
            </div>
            {groupedFacts.length === 0 ? (
              <div className="empty-state empty-state--compact">
                <p>{t('patients.noProfileFacts')}</p>
              </div>
            ) : (
              groupedFacts.map((group) => (
                <div key={group.category} className="profile-fact-group">
                  <h3>{getProfileCategoryLabel(group.category)}</h3>
                  {group.facts.map((fact) => (
                    <div key={fact.id} className="profile-fact-row">
                      <span>{fact.label || getProfileCategoryLabel(group.category)}</span>
                      <strong>{fact.value || '-'}</strong>
                      <small>{fact.status || t('common.unknown')}</small>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </aside>

        <section className="surface-panel patient-timeline-panel">
          <div className="surface-panel__body stack">
            <div className="section-heading">
              <h2>{t('patients.visitTimeline')}</h2>
              <span className="table-meta">{visits.length} {t('patients.visits')}</span>
            </div>

            {visits.length === 0 ? (
              <div className="empty-state">
                <h3>{t('patients.noVisits')}</h3>
                <p>{t('patients.noVisitsHint')}</p>
              </div>
            ) : (
              <div className="patient-timeline">
                {visits.map((item) => {
                  const visit = item.visit;
                  const reports = [...(item.reports || [])].sort((left, right) => {
                    const priority = (report) => {
                      if (report.is_effective_report) return 0;
                      if (report.report_type === 'final' && report.status === 'approved') return 1;
                      if (report.report_type === 'final') return 2;
                      return 3;
                    };
                    return priority(left) - priority(right);
                  });
                  const snapshot = item.snapshot;
                  return (
                    <article key={visit.id} className="patient-timeline-item">
                      <div className="patient-timeline-item__dot" />
                      <div className="patient-timeline-item__content">
                        <header className="patient-timeline-item__header">
                          <div className="table-composite">
                            <span className="table-title">{t('patients.visit')} #{visit.id}</span>
                            <span className="table-meta">
                              {formatPatientDateTime(visit.start_time)} · {visit.clinic_name || t('common.unknown')}
                            </span>
                          </div>
                          <div className="patient-timeline-item__badges">
                            <span className="status-pill status-pill--cool">{formatVisitType(visit.visit_type)}</span>
                            <span className={getVisitStatusClass(visit.status)}>{formatVisitStatus(visit.status)}</span>
                            {snapshot ? (
                              <span className="status-pill status-pill--success">{t('patients.hasSnapshot')}</span>
                            ) : (
                              <span className="status-pill status-pill--warning">{t('patients.noSnapshot')}</span>
                            )}
                          </div>
                        </header>

                        <div className="patient-report-links">
                          {reports.length === 0 ? (
                            <span className="table-meta">{t('patients.noReports')}</span>
                          ) : (
                            reports.map((report) => (
                              <Link
                                key={report.id}
                                className="table-token table-token--action"
                                to={['pending', 'generating', 'generation_failed'].includes(report.status) ? `/reports/${report.id}/review` : `/reports/${report.id}/view`}
                              >
                                {report.report_type === 'final' ? t('reports.finalReport') : t('reports.proposalReport')} #{report.id}
                                {report.is_effective_report ? ` · ${t('reports.effectiveReport')}` : ''}
                              </Link>
                            ))
                          )}
                        </div>

                        {renderSnapshot(snapshot)}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <aside className="patient-inspector">
          <section className="surface-panel surface-panel--soft">
            <div className="surface-panel__body stack">
              <div className="section-heading">
                <h2>{t('patients.objectiveTrends')}</h2>
              </div>
              {(historyContext.objective_trends || []).length === 0 ? (
                <p className="table-meta">{t('patients.noTrends')}</p>
              ) : (
                <div className="trend-list">
                  {historyContext.objective_trends.map((trend) => (
                    <div key={trend.key} className="trend-row">
                      <div>
                        <strong>{trend.key}</strong>
                        <span>{trend.previous_value || '-'} → {trend.latest_value || '-'}</span>
                      </div>
                      <span className={getTrendClass(trend.trend)}>{trend.trend || t('common.unknown')}</span>
                      <p>{trend.hint}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="surface-panel surface-panel--soft">
            <div className="surface-panel__body stack">
              <div className="section-heading">
                <h2>{t('patients.confirmationHints')}</h2>
              </div>
              {(historyContext.confirmation_hints || []).length === 0 ? (
                <p className="table-meta">{t('patients.noConfirmationHints')}</p>
              ) : (
                <div className="hint-list">
                  {historyContext.confirmation_hints.map((hint, index) => (
                    <div key={`${hint}-${index}`} className="hint-row">
                      <span>{formatDateOnly(historyContext.visits?.[0]?.date)}</span>
                      <p>{hint}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default PatientDetail;
